import './login.scss';
import _ from 'lodash'
import { get } from 'lodash';
import { FORM_ERROR } from 'final-form';
import { race } from 'rxjs';
import { first, map, mapTo } from 'rxjs/operators';
import { Component } from 'vue-property-decorator';

import { AuthentificationMethod, User } from '@skinsoft/types'
import { userSelectors } from '@skinsoft/selectors';
import { AuthService, UserAuthenticatedResult } from '@skinsoft/auth';
import { fetch } from '@skinsoft/network';
import { fetchAllLocales } from '@skinsoft/i18n/src/fetch-locale';
import { InstitutionDataStore, TranslationDataStore } from '@skinsoft/entity';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { LoginForm } from '../login-form';

declare const API_ENDPOINT: string
declare const APP_NAME: string

@Component({
    components: {
        sLoginForm: LoginForm,
    },
    props: ['homeRoute'],
    template: require('./login.html'),
})
export class Login extends ReactiveVue {
    public $authService: AuthService;

    private homeRoute: string;
    private $institutionDataStore: InstitutionDataStore;
    private authentificationMethods: AuthentificationMethod[] = []
    private requestedTryphonLogin: boolean = false
    private loginErrorMessage: string = ''

    public created() {
        if (this.$route.query.fromSSO !== undefined) {
            console.log('fromSSO route detected')
            const ssoUserData = sessionStorage.getItem('ssoUserData')
            if (ssoUserData === null) {
               this.loginErrorMessage = this.$t('auth.sso.errors.authenticationFailed') as string
            }
        }
        this.getAuthentificationMethods()
        this.subscribeTo(this.$authService.user$, this.onUser);
    }

    private get SSOAvailable(): boolean {
        return this.authentificationMethods?.length > 1
            && !this.requestedTryphonLogin
    }

    private getAuthentificationMethods() {
        this.$authService.getAuthentificationMethods().subscribe(authentificationMethods => {
            if(authentificationMethods) {
                this.authentificationMethods = authentificationMethods
                this.$forceUpdate()
            }
        })
    }

    onClickAuthenticationMethod(am: AuthentificationMethod) {
        if (am && am.name) {
            if (am.samlEndpoint) {
                const appname = APP_NAME.toLowerCase().replace('_', '-')
                const reporterEndpoint = API_ENDPOINT.replace('api.', `${appname}.`)

                window.fetch(
                    reporterEndpoint + '/saml2/authenticate/' + am.samlEndpoint,
                    {
                        credentials: 'include'
                    }
                )
                    .then(async response => {
                        if(response.status && response.status >= 200 && response.status < 300) {
                            if(response.headers) {
                                // @ts-ignore
                                const setCookies = response.headers.getSetCookie()

                                const html = await response.text()
                                try {
                                    const { action, samlRequest, relayState } = this.getSSORequestFromHtml(html)
                                    
                                    const form = document.createElement("form")
                                    form.setAttribute("method", "post")
                                    form.setAttribute("action", action)
                                    form.setAttribute("style", "display: none")
    
                                    const samlRequestInput = document.createElement("input")
                                    samlRequestInput.setAttribute("name", "SAMLRequest")
                                    samlRequestInput.setAttribute("value", samlRequest)
    
                                    const relayStateInput2 = document.createElement("input")
                                    relayStateInput2.setAttribute("name", "RelayState")
                                    relayStateInput2.setAttribute("value", relayState)
    
                                    form.appendChild(samlRequestInput)
                                    form.appendChild(relayStateInput2)
    
    
                                    document.getElementsByTagName('body')[0].appendChild(form);
                                    form.submit()
                                    return
                                } catch(err) {
                                    this.loginErrorMessage = this.$t('auth.sso.errors.authenticationFailed') as string
                                }
                            }
                        }
                        this.loginErrorMessage = this.$t('auth.sso.errors.authenticationFailed') as string
                    })
                    .catch(err => {
                        this.loginErrorMessage = this.$t('auth.sso.errors.authenticationFailed') as string
                    })

            } else {
                this.requestedTryphonLogin = true
            }
        }
    }

    public async onSubmit(values: Record<'username' | 'password', string>) {
        await this.$authService.logIn(values.username, values.password);

        const success$ = this.$authService.waitFor$('authenticated').pipe(mapTo(null));
        const error$ = this.$authService.waitFor$('unauthenticated').pipe(
            map(value => {
                const [, event] = value;
                const error = get(event, 'query.error', null);

                if (!error) {
                    return null;
                }

                return {
                    [FORM_ERROR]: fetch.isNetworkError(error)
                        ? 'auth.login.error.network'
                        : 'auth.login.error.credentials',
                };
            }),
        );

        return await race(success$, error$)
            .pipe(first())
            .toPromise();
    }

    private async onUser(authResult: UserAuthenticatedResult | null) {
        if (authResult && authResult.user) {
            // setting workspace
            if(localStorage.getItem('workspace') == null){
                localStorage.setItem('workspace', userSelectors.defaultWorkspace(authResult.user).id)
            }
            
            // setting institution
            this.$institutionDataStore.fetchAllByWorkspace(localStorage.getItem('workspace') as string).subscribe(
                institution => {
                    localStorage.setItem('institution', _.get(institution, '_embedded.institution[0].id'));
                    return fetch.api(`/users/token?institutionId=${_.get(institution, '_embedded.institution[0].id')}&workspaceId=${localStorage.getItem('workspace')}`,
                    {
                        method: 'GET',
                    })
                }
            )

            // setting locale
            const userLocale = userSelectors.defaultInterfaceLanguage(authResult.user);
            fetchAllLocales(Object.keys(this.$i18n.messages)).subscribe()
            this.$root.$i18n.locale = userLocale
            localStorage.setItem('preferredLocale', userLocale)

            // setting date format
            const localStorageDateFormat = localStorage.getItem('dateFormat')
            if(authResult.authMethod === 'SSO' && localStorageDateFormat) {
                localStorage.setItem('dateFormat', this.cleanDateFormat(localStorageDateFormat))
            } else {
    
                TranslationDataStore.getInstance().getDateFormat().subscribe(dateFormat => {
                    if(dateFormat) {
                        // adapt date format to luxon
                        const cleanedDateFormat = this.cleanDateFormat(dateFormat as string)
                        console.info('Saving date format in local storage: ', cleanedDateFormat)
                        localStorage.setItem('dateFormat', cleanedDateFormat)
                    } else {
                        console.error('Failed to retrieve date format from server. Date format: ', dateFormat)
                    }
                })
            }

            this.$router.push({ name: this.homeRoute });
            return;
        }
    }

    private cleanDateFormat(dateFormat: string): string {
        // adapt date format to luxon
        let cleanedDateFormat = dateFormat
        // @ts-ignore : until update of typescript replaceAll will generate a linting error
        cleanedDateFormat = dateFormat.replaceAll('m', 'M')
        // @ts-ignore : until update of typescript replaceAll will generate a linting error
        cleanedDateFormat = cleanedDateFormat.replaceAll('Y', 'y')
        // @ts-ignore : until update of typescript replaceAll will generate a linting error
        cleanedDateFormat = cleanedDateFormat.replaceAll('D', 'd')

        return cleanedDateFormat
    }

    private getSSORequestFromHtml(html: string): { action: string, samlRequest: string,  relayState: string} {
        const formRegex = /<form [\s\S]*<\/form>/g
        const form = (html.match(formRegex) || [""])[0]
        if(form) {
            const actionRegex = /action="([^"])*"/g
            let action = (form.match(actionRegex) || [""])[0]
            if(action) {
                action = action.replace("action=", '')
                // @ts-ignore need to upgrade ts
                action = action.replaceAll('"', '')
                
                const samlInputRegex = /<input.*SAMLRequest.*\/>/g
                const samlInput = (form.match(samlInputRegex) || [""])[0]
                if(samlInput) {
                    const samlRequestRegex = /value="([^"])*"/g
                    let samlRequest = (samlInput.match(samlRequestRegex) || [""])[0]
                    if(samlRequest) {
                        samlRequest = samlRequest.replace("value=", '')
                        // @ts-ignore need to upgrade ts
                        samlRequest = samlRequest.replaceAll('"', '')
                        
                        const relayStateInputRegex = /<input.*RelayState.*\/>/g
                        const relayStateInput = (form.match(relayStateInputRegex) || [""])[0]
                        if(relayStateInput) {
                            const relayStateRegex = /value="([^"])*"/g
                            let relayState = (relayStateInput.match(relayStateRegex) || [""])[0]
                            if(relayState) {
                                relayState = relayState.replace("value=", '')
                                // @ts-ignore need to upgrade ts
                                relayState = relayState.replaceAll('"', '')

                                return {
                                    action,
                                    relayState,
                                    samlRequest
                                }
                            }
                        }
                    }
                }
            }
        } 
        throw new Error()       
    }
}
